import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { G21Wordmark } from "./G21Monogram";
import { useT, LanguageToggle } from "@/i18n/LanguageProvider";

export const Navbar = () => {
    const t = useT();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    const NAV_ITEMS = [
        { id: "wohnungen", label: t({ de: "Wohnungen", en: "Apartments" }) },
        { id: "vergleich", label: t({ de: "Vergleich", en: "Compare" }) },
        { id: "ausstattung", label: t({ de: "Ausstattung", en: "Equipment" }) },
        { id: "lage", label: t({ de: "Lage", en: "Location" }) },
        { id: "finanzierung", label: t({ de: "Finanzierung", en: "Financing" }) },
        { id: "ablauf", label: t({ de: "Ablauf", en: "Process" }) },
        { id: "faq", label: "FAQ" },
        { id: "kontakt", label: t({ de: "Kontakt", en: "Contact" }) },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // Element not on current page → navigate to root with hash
            window.location.href = `/#${id}`;
        }
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-cream/85 backdrop-blur-xl border-b border-stone-200"
                    : "bg-transparent"
            }`}
            data-testid="main-navbar"
        >
            <div className="container-page flex items-center justify-between h-24">
                <Link
                    to="/"
                    className="group inline-flex items-center"
                    data-testid="navbar-logo"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    aria-label="G21 – Startseite"
                >
                    <G21Wordmark
                        size={44}
                        className={`transition-colors ${scrolled ? "text-ink" : "text-white"}`}
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_ITEMS.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => scrollTo(item.id)}
                            className={`nav-link font-body text-sm tracking-wide transition-colors ${
                                scrolled ? "text-stone-700" : "text-white/90"
                            }`}
                            data-testid={`nav-${item.id}`}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-5">
                    <LanguageToggle
                        colorClass={scrolled ? "text-stone-600" : "text-white/80"}
                    />
                    <button
                        onClick={() => scrollTo("wohnungen")}
                        className={`px-5 py-2.5 text-sm font-body font-medium tracking-wide transition-all ${
                            scrolled
                                ? "bg-brand-500 text-white hover:bg-brand-600"
                                : "bg-white text-ink hover:bg-cream"
                        }`}
                        data-testid="navbar-cta"
                    >
                        {t({ de: "Wohnungen ansehen", en: "View apartments" })}
                    </button>
                </div>

                <button
                    className={`lg:hidden p-2 ${scrolled ? "text-ink" : "text-white"}`}
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menü"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-cream border-t border-stone-200" data-testid="mobile-menu">
                    <div className="container-page py-6 flex flex-col gap-4">
                        {NAV_ITEMS.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => scrollTo(item.id)}
                                className="text-left py-2 font-body text-stone-700 border-b border-stone-100"
                                data-testid={`mobile-nav-${item.id}`}
                            >
                                {item.label}
                            </button>
                        ))}
                        <div className="py-2 border-b border-stone-100">
                            <LanguageToggle colorClass="text-stone-700" />
                        </div>
                        <button
                            onClick={() => scrollTo("wohnungen")}
                            className="btn-primary w-full mt-2"
                            data-testid="mobile-cta"
                        >
                            {t({ de: "Wohnungen ansehen", en: "View apartments" })}
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
};
